import React, { useEffect, useRef } from 'react';

// Ambient field: a slowly-rotating ring of labelled data sources (Price, News, Flow,
// Sentiment, Fundamentals, Macro) streaming signals into a central neural core that
// absorbs and pulses, with a live candlestick chart building inside it. Deliberate,
// calm pace. Fills its parent (wrap in a `position: relative` box). Shared by the
// dashboard ambient field AND the full-page SplashScreen — single source of truth.
const SOURCES = ['Price', 'News', 'Order Flow', 'Sentiment', 'Fundamentals', 'Macro'];
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

export default function SignalField({ accent }) {
  const ref = useRef(null);
  useEffect(() => {
    const cv = ref.current; if (!cv) return undefined;
    const ctx = cv.getContext('2d');
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let w = 0, h = 0, raf = 0, cx = 0, cy = 0, rot = 0, ringRot = 0, pulse = 0, frame = 0, parts = [];
    let R = 120, Rc = 54, candles = [], live = null, formT = 0, pitch = 6, maxv = 12, holdT = 0;
    const parent = cv.parentElement;
    const ANCH = [0.30, 0.52, 0.27, 0.63, 0.40, 0.76, 0.55];
    const path = (i) => {
      const t = maxv <= 1 ? 0 : Math.min(1, i / (maxv - 1));
      const seg = t * (ANCH.length - 1), k = Math.min(ANCH.length - 2, Math.floor(seg)), f = seg - k, s = f * f * (3 - 2 * f);
      return ANCH[k] * (1 - s) + ANCH[k + 1] * s;
    };
    const mk = (o, i) => { const spike = Math.random() < 0.14 ? (Math.random() < 0.5 ? 0.16 : -0.16) : 0; return { o, c: o, h: o, l: o, target: clamp(path(i) + spike + (Math.random() - 0.5) * 0.04, 0.08, 0.92) }; };
    const size = () => {
      const r = parent.getBoundingClientRect(); const dpr = Math.min(2, window.devicePixelRatio || 1);
      cv.width = r.width * dpr; cv.height = r.height * dpr; ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      w = r.width; h = r.height; cx = w / 2; cy = h / 2;
      R = clamp(Math.min(w, h) * 0.34, 88, 165); Rc = R * 0.5;
      pitch = 6; maxv = Math.max(8, Math.floor((Rc * 1.6) / pitch) + 1);
      candles = []; live = mk(path(0), 0); formT = 0; holdT = 0;
    };
    const srcPos = (i) => { const a = (i / SOURCES.length) * Math.PI * 2 + rot; return { x: cx + Math.cos(a) * R, y: cy + Math.sin(a) * R, a }; };
    const draw = () => {
      frame += 1; rot += 0.0016; ringRot += 0.006; pulse *= 0.95;
      ctx.clearRect(0, 0, w, h);
      const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, Rc * (1.7 + pulse * 0.5));
      g.addColorStop(0, accent); g.addColorStop(1, 'transparent');
      ctx.globalAlpha = 0.1 + pulse * 0.14; ctx.fillStyle = g; ctx.fillRect(0, 0, w, h);
      ctx.font = '600 11px ui-monospace, Menlo, monospace'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      SOURCES.forEach((cat, i) => {
        const s = srcPos(i);
        ctx.globalAlpha = 0.08; ctx.strokeStyle = accent; ctx.lineWidth = 1;
        ctx.beginPath(); ctx.moveTo(s.x, s.y); ctx.lineTo(cx, cy); ctx.stroke();
        ctx.globalAlpha = 0.55; ctx.fillStyle = accent; ctx.beginPath(); ctx.arc(s.x, s.y, 2.3, 0, 7); ctx.fill();
        const lr = R + 25; const lx = cx + Math.cos(s.a) * lr, ly = cy + Math.sin(s.a) * lr;
        ctx.globalAlpha = 0.58; ctx.fillText(cat.toUpperCase(), clamp(lx, 52, w - 52), clamp(ly, 12, h - 12));
      });
      if (!reduce && frame % 30 === 0 && parts.length < 22) { const s = srcPos(Math.floor(Math.random() * SOURCES.length)); parts.push({ x: s.x, y: s.y, px: s.x, py: s.y, tx: cx + (Math.random() - 0.5) * Rc * 0.5, ty: cy + (Math.random() - 0.5) * Rc * 0.5, p: 0 }); }
      ctx.save(); ctx.translate(cx, cy); ctx.rotate(ringRot);
      ctx.globalAlpha = 0.22; ctx.strokeStyle = accent; ctx.lineWidth = 1; ctx.setLineDash([4, 7]);
      ctx.beginPath(); ctx.arc(0, 0, Rc, 0, Math.PI * 2); ctx.stroke(); ctx.setLineDash([]); ctx.restore();
      const FORM = 165;
      if (holdT > 0) {
        holdT -= 1;
        if (holdT === 0) { candles = []; live = mk(path(0), 0); formT = 0; }
      } else if (!reduce && live) {
        live.c = Math.min(0.92, Math.max(0.08, live.c + (live.target - live.c) * 0.05 + (Math.random() - 0.5) * 0.018));
        live.h = Math.max(live.h, live.c); live.l = Math.min(live.l, live.c); formT += 1;
        if (formT >= FORM) {
          formT = 0;
          const body = Math.abs(live.c - live.o);
          candles.push({ ...live, sig: body > 0.13 ? (live.c >= live.o ? 'buy' : 'sell') : null, anim: 0 });
          if (candles.length >= maxv) { holdT = 130; live = null; } else { live = mk(live.c, candles.length); }
        }
      } else if (reduce && candles.length === 0) {
        let o = path(0); for (let i = 0; i < maxv; i++) { const c = path(i); candles.push({ o, c, h: Math.max(o, c) + 0.02, l: Math.min(o, c) - 0.02, sig: null }); o = c; }
      }
      const series = live ? [...candles, live] : candles;
      const M = series.length, bw = 3, chartW = (maxv - 1) * pitch, leftX = cx - chartW / 2;
      const py = (p) => cy - (p - 0.5) * Rc * 1.25;
      ctx.save(); ctx.beginPath(); ctx.arc(cx, cy, Rc, 0, Math.PI * 2); ctx.clip();
      for (let i = 0; i < M; i++) {
        const cd = series[i], x = leftX + i * pitch;
        const up = cd.c >= cd.o, col = up ? '#3fae6e' : '#d1594f';
        ctx.globalAlpha = 0.92; ctx.strokeStyle = col; ctx.fillStyle = col; ctx.lineWidth = 1;
        ctx.beginPath(); ctx.moveTo(x, py(cd.h)); ctx.lineTo(x, py(cd.l)); ctx.stroke();
        const yo = py(cd.o), yc = py(cd.c); ctx.fillRect(x - bw / 2, Math.min(yo, yc), bw, Math.max(1.4, Math.abs(yc - yo)));
        if (cd.sig) {
          cd.anim = Math.min(1, (cd.anim || 0) + 0.018); const a = cd.anim, sz = 8 * a;
          if (cd.sig === 'buy') { const my = py(cd.l) + 10 + (1 - a) * 12; ctx.globalAlpha = a; ctx.fillStyle = '#3fae6e'; ctx.beginPath(); ctx.moveTo(x, my - sz); ctx.lineTo(x - sz * 0.72, my); ctx.lineTo(x + sz * 0.72, my); ctx.closePath(); ctx.fill(); }
          else { const my = py(cd.h) - 10 - (1 - a) * 12; ctx.globalAlpha = a; ctx.fillStyle = '#d1594f'; ctx.beginPath(); ctx.moveTo(x, my + sz); ctx.lineTo(x - sz * 0.72, my); ctx.lineTo(x + sz * 0.72, my); ctx.closePath(); ctx.fill(); }
        }
      }
      ctx.restore();
      ctx.lineWidth = 1.3;
      parts = parts.filter((s) => {
        s.p += 0.0055; const e = 1 - Math.pow(1 - Math.min(1, s.p), 2);
        const nx = s.x + (s.tx - s.x) * e, ny = s.y + (s.ty - s.y) * e;
        ctx.globalAlpha = 0.5 * (0.3 + s.p * 0.7); ctx.strokeStyle = accent;
        ctx.beginPath(); ctx.moveTo(s.px, s.py); ctx.lineTo(nx, ny); ctx.stroke();
        ctx.globalAlpha = 0.85; ctx.fillStyle = accent; ctx.beginPath(); ctx.arc(nx, ny, 1.5, 0, 7); ctx.fill();
        s.px = nx; s.py = ny;
        if (s.p >= 1) { pulse = Math.min(1.4, pulse + 0.14); return false; }
        return true;
      });
      ctx.globalAlpha = 1; raf = window.requestAnimationFrame(draw);
    };
    size(); const ro = new ResizeObserver(size); ro.observe(parent);
    if (reduce) draw(); else raf = window.requestAnimationFrame(draw);
    return () => { window.cancelAnimationFrame(raf); ro.disconnect(); };
  }, [accent]);
  return <canvas ref={ref} className="absolute inset-0 w-full h-full" aria-hidden="true" />;
}
